import { useParams, Link } from 'react-router-dom';
import { PLATFORM_LABELS } from '@affily/shared/constants';
import { formatCents, formatCount, formatPercent, bpsToPercent } from '@affily/shared/money';
import { useFetch } from '../hooks/useFetch.js';
import { useAuth } from '../hooks/useAuth.jsx';
import { Seo } from '../components/Seo.jsx';
import { TransparencyBadge, SelfReported, Tracked } from '../components/Transparency.jsx';
import { Reviews } from '../components/Reviews.jsx';
import { Button, Badge, Spinner, Unknown } from '../components/ui.jsx';

/** "25 Oct": when a verified number was last read, short enough for a table cell. */
const shortDay = (date) =>
  new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

/**
 * The public creator profile.
 *
 * Rates are visible to everyone. Audience stats are visible to everyone AND
 * labelled self-reported at every single render site — the label travels with
 * the number rather than sitting in a footnote nobody reads.
 */
export default function CreatorDetail() {
  const { slug } = useParams();
  const { user, isBrand } = useAuth();
  const { data, error, loading } = useFetch(`/creators/${slug}`);

  if (loading) {
    return (
      <div className="flex min-h-96 items-center justify-center text-ink-faint">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="text-2xl font-semibold">{error.message}</h1>
        <Button to="/creators" variant="outline" className="mt-6">
          Browse all creators
        </Button>
      </div>
    );
  }

  const c = data.creator;
  const cheapestRate = c.startingRates.length
    ? Math.min(...c.startingRates.map((r) => r.amountCents))
    : null;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: c.displayName,
    description: c.headline ?? c.bio ?? undefined,
    image: c.avatarUrl ?? undefined,
    address: c.location ?? undefined,
    sameAs: c.platforms.map((p) => p.url).filter(Boolean),
  };

  return (
    <>
      <Seo
        title={`${c.displayName}${c.headline ? ` — ${c.headline}` : ''}`}
        description={`${c.displayName}: ${formatCount(c.totalFollowers)} followers across ${c.platforms.length} platform${c.platforms.length === 1 ? '' : 's'}${cheapestRate ? `, rates from ${formatCents(cheapestRate)}` : ''}. Public rates, ${c.platforms.some((p) => p.isSelfReported === false) ? 'verified' : 'self-reported'} audience stats, transparency score ${c.transparency.score}/100.`}
        type="profile"
        jsonLd={jsonLd}
      />

      <article className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <nav className="text-sm text-ink-muted">
          <Link to="/creators" className="hover:text-ink">
            Creators
          </Link>
        </nav>

        <header className="mt-4 flex flex-wrap items-start justify-between gap-6">
          <div className="flex min-w-0 gap-4">
            {c.avatarUrl ? (
              <img
                src={c.avatarUrl}
                alt=""
                className="size-20 shrink-0 rounded-xl border border-rule object-cover"
              />
            ) : (
              <div className="flex size-20 shrink-0 items-center justify-center rounded-xl border border-rule bg-paper-sunken text-2xl font-semibold text-ink-faint">
                {c.displayName.charAt(0)}
              </div>
            )}
            <div className="min-w-0">
              <h1 className="text-3xl font-semibold tracking-tight">{c.displayName}</h1>
              {c.headline && <p className="mt-1 text-ink-muted">{c.headline}</p>}
              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                <TransparencyBadge transparency={c.transparency} size="sm" showDetail={false} />
                {c.isAvailable ? (
                  <Badge tone="positive">Available for partnerships</Badge>
                ) : (
                  <Badge>Not currently available</Badge>
                )}
                {c.location && <Badge>{c.location}</Badge>}
                {c.niches.map((n) => (
                  <Badge key={n} className="capitalize">
                    {n}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {isBrand && (
            <Button to={`/dashboard/brand/invite/${c.slug}`} size="lg">
              Invite to a program
            </Button>
          )}
          {!user && (
            <div className="text-right">
              <Button to="/signup?role=brand" size="lg" variant="outline">
                Join as a brand to invite
              </Button>
              {c.startingRates.length > 0 && (
                <p className="mt-1.5 text-xs text-ink-faint">Rates below are already public.</p>
              )}
            </div>
          )}
        </header>

        {c.bio && <p className="mt-8 max-w-2xl whitespace-pre-line text-ink-muted">{c.bio}</p>}

        {/* ── Rates (left out until the creator sets some) ───────────────── */}
        {c.startingRates.length > 0 && (
          <section className="mt-10">
            <div className="mb-2 flex items-baseline justify-between gap-4">
              <h2 className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                Starting rates · public
              </h2>
              <p className="text-xs text-ink-faint">Set by the creator. No “DM for pricing”.</p>
            </div>
            <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Deliverable</th>
                    <th className="num">Starting at</th>
                  </tr>
                </thead>
                <tbody>
                  {c.startingRates.map((r, i) => (
                    <tr key={i}>
                      <td>
                        {r.deliverable}
                        {r.note && <span className="block text-xs text-ink-faint">{r.note}</span>}
                      </td>
                      <td className="num font-medium">{formatCents(r.amountCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* ── Audience ──────────────────────────────────────────────────── */}
        <section className="mt-10">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
              Audience
            </h2>
            <SelfReported />
          </div>
          <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Platform</th>
                  <th>Handle</th>
                  <th className="num">Followers</th>
                  <th className="num">Avg. views</th>
                  <th className="num">Engagement</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {c.platforms.map((p, i) => (
                  <tr key={i}>
                    <td className="font-medium">{PLATFORM_LABELS[p.platform] ?? p.platform}</td>
                    <td>
                      {p.url ? (
                        <a
                          href={p.url}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="text-accent hover:underline"
                        >
                          {p.handle ?? 'View profile'}
                        </a>
                      ) : (
                        <span className="text-ink-faint">{p.handle ?? <Unknown reason="Not linked" />}</span>
                      )}
                    </td>
                    <td className="num">
                      {p.followers == null ? <Unknown reason="Not reported" /> : formatCount(p.followers)}
                    </td>
                    <td className="num">
                      {p.avgViews == null ? <Unknown reason="Not reported" /> : formatCount(p.avgViews)}
                    </td>
                    <td className="num">
                      {p.engagementRateBps == null ? (
                        <Unknown reason="Not reported" />
                      ) : (
                        formatPercent(bpsToPercent(p.engagementRateBps))
                      )}
                    </td>
                    <td>
                      {/* The label travels with the number, on every row. */}
                      {p.isSelfReported ? (
                        <SelfReported />
                      ) : (
                        <Tracked
                          title={`Read from ${PLATFORM_LABELS[p.platform]} with the creator’s permission, and refreshed daily.`}
                        >
                          Verified{p.verifiedAt && ` · ${shortDay(p.verifiedAt)}`}
                        </Tracked>
                      )}
                    </td>
                  </tr>
                ))}
                {c.platforms.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-sm text-ink-muted">
                      No platforms listed yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-ink-faint">
            These numbers come from the creator, not from a platform API. Every listed profile links
            out so you can check them yourself — which is the point of requiring the link.
          </p>
        </section>

        {/* ── Demographics ──────────────────────────────────────────────── */}
        {(c.demographics.topCountries.length > 0 || c.demographics.ageSplit.length > 0) && (
          <section className="mt-10 grid gap-6 sm:grid-cols-2">
            {c.demographics.topCountries.length > 0 && (
              <Distribution
                title="Top countries"
                rows={c.demographics.topCountries.map((x) => ({ label: x.code, pct: x.pct }))}
              />
            )}
            {c.demographics.ageSplit.length > 0 && (
              <Distribution
                title="Age split"
                rows={c.demographics.ageSplit.map((x) => ({ label: x.bucket, pct: x.pct }))}
              />
            )}
          </section>
        )}

        {/* ── Track record on Affily ────────────────────────────────────── */}
        <section className="mt-10">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
              Track record on Affily
            </h2>
            <Tracked />
          </div>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-3">
            <Stat label="Completed partnerships" value={formatCount(c.verified.completedPartnerships)} />
            <Stat
              label="Average rating"
              value={
                c.verified.ratingCount === 0 ? null : `${(c.verified.ratingAvgBps / 100).toFixed(1)} / 5`
              }
              empty="No completed partnerships have been reviewed yet"
            />
            <Stat label="Reviews" value={formatCount(c.verified.ratingCount)} />
          </div>
        </section>

        {/* ── Portfolio ─────────────────────────────────────────────────── */}
        {c.portfolio.length > 0 && (
          <section className="mt-10">
            <h2 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-ink-faint">
              UGC portfolio
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {c.portfolio.map((item, i) => (
                <figure key={i} className="overflow-hidden rounded-lg border border-rule bg-paper-raised">
                  {item.type === 'video' ? (
                    <video src={item.url} controls className="aspect-[4/5] w-full object-cover" />
                  ) : (
                    <img
                      src={item.url}
                      alt={item.caption ?? ''}
                      loading="lazy"
                      className="aspect-[4/5] w-full object-cover"
                    />
                  )}
                  {item.caption && (
                    <figcaption className="px-2 py-1.5 text-xs text-ink-muted">{item.caption}</figcaption>
                  )}
                </figure>
              ))}
            </div>
          </section>
        )}

        <Reviews subjectType="creator" slug={c.slug} />

        {/* ── Past collaborations ───────────────────────────────────────── */}
        {c.pastCollaborations.length > 0 && (
          <section className="mt-10">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                Past collaborations
              </h2>
              <SelfReported />
            </div>
            <ul className="flex flex-wrap gap-1.5">
              {c.pastCollaborations.map((collab, i) => (
                <li key={i}>
                  {collab.url ? (
                    <a href={collab.url} target="_blank" rel="noopener noreferrer nofollow">
                      <Badge className="hover:border-accent">{collab.brandName}</Badge>
                    </a>
                  ) : (
                    <Badge>{collab.brandName}</Badge>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </>
  );
}

function Stat({ label, value, empty }) {
  return (
    <div className="bg-paper-raised px-4 py-3">
      <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">{label}</p>
      <p className="mt-1 text-lg font-medium tabular-nums">
        {value ?? <Unknown reason={empty} />}
      </p>
    </div>
  );
}

function Distribution({ title, rows }) {
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h2 className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">{title}</h2>
        <SelfReported />
      </div>
      <div className="rounded-lg border border-rule bg-paper-raised p-3">
        <ul className="space-y-2">
          {rows.map((row) => (
            <li key={row.label} className="flex items-center gap-3 text-sm">
              <span className="w-14 shrink-0 font-medium">{row.label}</span>
              <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-paper-sunken">
                <span
                  className="block h-full rounded-full bg-accent"
                  style={{ width: `${Math.min(100, row.pct)}%` }}
                />
              </span>
              <span className="w-11 shrink-0 text-right tabular-nums text-ink-muted">
                {row.pct}%
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
